import { createContext, ReactNode, useEffect, useState } from 'react'
import { useDigitaneApi } from '../project/digitaneApi'
import { SiteType, CourseDetailsList } from '../../openapi'
import { setSentryUser, clearSentryUser } from '../../utils/sentryUser'

interface UserContextType {
  userId: string
  siteName: SiteType | undefined
  nickname?: string | null
  membershipType?: string | null
  isOnlineUser: boolean
  isLicenseUser: boolean
  loading: boolean
  getAllMissions: () => Promise<CourseDetailsList | undefined>
}

export const UserContext = createContext<UserContextType>({
  userId: '',
  siteName: undefined,
  nickname: null,
  membershipType: null,
  isOnlineUser: false,
  isLicenseUser: false,
  loading: true,
  getAllMissions: async () => undefined,
})

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { getUserInfo, getAllMissions } = useDigitaneApi()
  const [userId, setUserId] = useState('')
  const [siteName, setSiteName] = useState<SiteType | undefined>(undefined)
  const [nickname, setNickname] = useState<string | null>(null)
  const [membershipType, setMembershipType] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const [isOnlineUser, setIsOnlineUser] = useState<boolean>(false)
  const [isLicenseUser, setIsLicenseUser] = useState<boolean>(false)

  useEffect(() => {
    setIsOnlineUser(siteName === SiteType.Online)
    setIsLicenseUser(siteName === SiteType.License)
  }, [siteName])

  // Sync user information with Sentry for error tracking
  useEffect(() => {
    if (userId && siteName) {
      const userInfo = {
        id: parseInt(userId) || 0,
        nickname,
        membershipType,
        siteName: siteName === SiteType.Online ? 'online' : 'license',
      }
      setSentryUser(userInfo, siteName)
    } else {
      clearSentryUser()
    }
  }, [userId, nickname, membershipType, siteName])

  useEffect(() => {
    const fetchUserInfo = async () => {
      setLoading(true)
      try {
        const result = await getUserInfo()
        const user = result?.user
        if (user) {
          const userIdStr = user.id.toString()
          const userSiteName =
            user.siteName === 'online' ? SiteType.Online : SiteType.License

          setUserId(userIdStr)
          setSiteName(userSiteName)
          setNickname(user.nickname || null)
          setMembershipType(user.membershipType || null)
        }
      } finally {
        setLoading(false)
      }
    }
    fetchUserInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <UserContext.Provider
      value={{
        userId,
        siteName,
        nickname,
        membershipType,
        isOnlineUser,
        isLicenseUser,
        loading,
        getAllMissions,
      }}
    >
      {loading ? null : children}
    </UserContext.Provider>
  )
}
